'use client';

import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { PromptInputBox } from './ai-prompt-box';

interface _DemoMessage {
  text: string;
  delay: number;
}

interface DemoStep {
  message: string;
  response: {
    type: 'image' | 'text';
    content: string;
    thinkingText?: string;
  };
}

const demoSteps: DemoStep[] = [
  {
    message: "Hello Aura, fold the GFP protein.",
    response: {
      type: 'image',
      content: "/GFP.png",
      thinkingText: "Using Boltz-2"
    }
  },
  {
    message: "What is the catalytic activity?",
    response: {
      type: 'text',
      content: "This protein exhibits catalytic activity through a process known as protein self-fluorescence. It catalyzes the reaction between dinoflagellate luciferin and oxygen, resulting in the production of carbon dioxide, light (hnu), and an oxidized form of dinoflagellate luciferin.",
      thinkingText: "Using ESM-1 with Evolla"
    }
  },
  {
    message: "What are the confidence scores?",
    response: {
      type: 'text',
      content: "The protein structure shows excellent confidence scores with pLDDT > 90 for most regions. The beta-barrel fold is well-defined with high structural reliability.",
      thinkingText: "Using metrics context"
    }
  }
];

export const AnimatedDemo: React.FC = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [_currentText, setCurrentText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  const [boxPosition, setBoxPosition] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [_typingCursor, setTypingCursor] = useState(true);
  const [_responseText, setResponseText] = useState("");
  const [_isTypingResponse, setIsTypingResponse] = useState(false);
  
  const animationRef = useRef<HTMLDivElement>(null);
  const typingIntervalRef = useRef<NodeJS.Timeout>();
  const animationTimeoutRef = useRef<NodeJS.Timeout>();
  const cursorIntervalRef = useRef<NodeJS.Timeout>();
  const responseTypingIntervalRef = useRef<NodeJS.Timeout>();
  const currentIndexRef = useRef(0); // Track current index with ref
  const animateMessageRef = useRef<((messageIndex?: number) => void) | null>(null);

  const simulateSending = useCallback(() => {
    // Clear any existing intervals before starting new animation
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    if (responseTypingIntervalRef.current) {
      clearInterval(responseTypingIntervalRef.current);
    }
    
    setIsSending(true);
    
    // Simulate sending delay - much faster
    setTimeout(() => {
      setIsSending(false);
      
      // Move box down more to make room for responses above
      setTimeout(() => {
        // Move down more for images to create better spacing
        const currentResponse = demoSteps[currentIndexRef.current];
        const moveDistance = currentResponse.response.type === 'image' ? 150 : 80;
        setBoxPosition(moveDistance);
        setShowResponse(true);
        
        // Responses are now predefined - no need for text animation
        
        // Show response for longer time
        setTimeout(() => {
          setShowResponse(false);
          setBoxPosition(0);
          setResponseText("");
          setIsTypingResponse(false);
          
          // Move to next message quickly
          setTimeout(() => {
            const nextIndex = (currentIndexRef.current + 1) % demoSteps.length;
            currentIndexRef.current = nextIndex; // Update ref immediately
            setCurrentMessageIndex(nextIndex);
            setCurrentResponseIndex(nextIndex);
            
            // Call animateMessage with the next index directly
            if (animateMessageRef.current) {
              animateMessageRef.current(nextIndex);
            }
          }, 300);
        }, 1999);
      }, 300);
    }, 200);
  }, []);

  const animateMessage = useCallback((messageIndex?: number) => {
    // Clear any existing typing interval first
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    
    const index = messageIndex ?? currentMessageIndex;
    const step = demoSteps[index];
    setIsTyping(true);
    setCurrentText("");
    setInputValue("");
    
    let charIndex = 0;
    const typeSpeed = 30; // ms per character - faster typing for better demo flow
    
    typingIntervalRef.current = setInterval(() => {
      if (charIndex < step.message.length) {
        const newChar = step.message[charIndex];
        setCurrentText(prev => prev + newChar);
        setInputValue(prev => prev + newChar);
        charIndex++;
      } else {
        clearInterval(typingIntervalRef.current!);
        setIsTyping(false);
        
        // Wait a bit then simulate sending
        setTimeout(() => {
          simulateSending();
        }, 150);
      }
    }, typeSpeed);
  }, [currentMessageIndex, simulateSending]);

  // Assign animateMessage to ref so simulateSending can call it
  animateMessageRef.current = animateMessage;

  const _animateResponseText = useCallback((text: string) => {
    setIsTypingResponse(true);
    setResponseText("");
    
    let charIndex = 0;
    let currentText = "";
    const typeSpeed = 40; // Slightly slower for response text
    
    responseTypingIntervalRef.current = setInterval(() => {
      if (charIndex < text.length) {
        currentText += text[charIndex];
        setResponseText(currentText);
        charIndex++;
      } else {
        clearInterval(responseTypingIntervalRef.current!);
        setIsTypingResponse(false);
      }
    }, typeSpeed);
  }, []);

  // Start the animation loop
  useEffect(() => {
    // Start the first animation
    animateMessage();
    
    // Cursor blinking effect
    cursorIntervalRef.current = setInterval(() => {
      setTypingCursor(prev => !prev);
    }, 500);
    
    // Capture ref values for cleanup
    const currentTypingInterval = typingIntervalRef.current;
    const currentAnimationTimeout = animationTimeoutRef.current;
    const currentCursorInterval = cursorIntervalRef.current;
    const currentResponseTypingInterval = responseTypingIntervalRef.current;
    
    return () => {
      if (currentTypingInterval) clearInterval(currentTypingInterval);
      if (currentAnimationTimeout) clearTimeout(currentAnimationTimeout);
      if (currentCursorInterval) clearInterval(currentCursorInterval);
      if (currentResponseTypingInterval) clearInterval(currentResponseTypingInterval);
    };
  }, []); // Empty dependency array to run only once


  const handleSend = (_message: string, _files?: File[]) => {
    // This won't be called during demo, but keeping for safety
  };

  const handleInputChange = (value: string) => {
    // During demo, we control the input value
    if (!isTyping) {
      setInputValue(value);
    }
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Animated PromptInputBox - starts centered, moves down when showing results */}
      <div 
        ref={animationRef}
        className="transition-all duration-1000 ease-in-out"
        style={{ transform: `translateY(${boxPosition}px)` }}
      >
        <div className="relative">
          <PromptInputBox
            onSend={handleSend}
            isLoading={isSending}
            placeholder="Type your message here..."
            className="w-[400px] pointer-events-none"
            value={inputValue}
            onValueChange={handleInputChange}
          />
          {/* Moving cursor that follows the text */}
          {isTyping && (
            <div 
              className="absolute top-0 left-0 w-0.5 h-6 bg-black animate-pulse"
              style={{ 
                left: `${Math.min(inputValue.length * 8.5 + 16, 360)}px`,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            />
          )}
        </div>
      </div>

      {/* Response overlay - positioned above the PromptInputBox with upward animation */}
      {showResponse && (
        <div 
          className="absolute inset-0 flex justify-center pointer-events-none"
          style={{ 
            bottom: '50%',
            transform: 'translateY(80px)'
          }}
        >
          <div className="relative animate-in slide-in-from-bottom-4 duration-700">
            <div className="space-y-4">
              {/* Thinking step with animated dots - liquid glass effect */}
              <div className="bg-blue-50/20 backdrop-blur-md border border-blue-200/30 rounded-[3rem] shadow-2xl py-4 max-w-[300px] animate-in slide-in-from-bottom-4 duration-700 relative overflow-hidden">
                {/* Liquid glass border effect */}
                <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-blue-200/20 via-transparent to-blue-100/10 pointer-events-none"></div>
                <div className="relative flex items-center justify-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce shadow-lg" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce shadow-lg" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce shadow-lg" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-blue-800 text-sm font-medium text-center">
                    {demoSteps[currentResponseIndex].response.thinkingText || "Thinking..."}
                  </span>
                </div>
              </div>
              
              {/* Response content - image or text */}
              {demoSteps[currentResponseIndex].response.type === 'image' ? (
                <Image
                  src={demoSteps[currentResponseIndex].response.content}
                  alt="Demo result"
                  width={400}
                  height={400}
                  className="w-[400px] h-[400px] object-contain rounded-2xl shadow-2xl animate-in zoom-in-95 duration-500 delay-300"
                />
              ) : (
                <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-[3rem] shadow-2xl p-6 max-w-[500px] animate-in slide-in-from-bottom-4 duration-700 delay-300 relative overflow-hidden">
                  {/* Liquid glass border effect */}
                  <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-white/20 via-transparent to-white/10 pointer-events-none"></div>
                  <div className="relative text-gray-800 leading-relaxed text-center min-h-[60px] flex items-center justify-center">
                    <span className="animate-in fade-in-0 duration-500 delay-500">
                      {demoSteps[currentResponseIndex].response.content}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
        {demoSteps.map((_, index) => (
          <div
            key={index}
            className={`w-3 h-3 rounded-full transition-all duration-500 ${
              index === currentMessageIndex 
                ? 'bg-white scale-125 shadow-lg' 
                : 'bg-white/40 scale-100'
            }`}
          />
        ))}
      </div>


    </div>
  );
};
